import { useId } from "react";
import dayjs from "dayjs";
import { Mui, useTheme } from "@leap4/frontend";
import { Area, CartesianGrid, ComposedChart, Line, ReferenceArea, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { numericFontFamily } from "../../../theme/colors";
import type { IPredictionSeriesPoint } from "../reporting/chart/reportChart";
import { PROJECTION_COLORS } from "./ProjectionEventCard";
import { formatProjected } from "./summarize";

const { Box, Paper, Typography } = Mui;

type Zone = "settled" | "settling" | "forecast";

interface IRow {
    date: string;
    zone: Zone;
    counted: number | null;
    expected: number | null;
    forecast: number | null;
    band: [number, number] | null;
    lower: number | null;
    upper: number | null;
    marginPct: number | null;
}

const zoneOf = (basis: string): Zone => (basis === "forecast" ? "forecast" : basis === "projected" ? "settling" : "settled");

/** Rows for one event; the boundary days carry both neighbouring lines so the strokes join rather than meet. */
const buildRows = (points: IPredictionSeriesPoint[], from: string, to: string): IRow[] => {
    const inRange = points.filter((p) => p.pixelDate >= from && p.pixelDate <= to).sort((a, b) => a.pixelDate.localeCompare(b.pixelDate));
    const rows: IRow[] = inRange.map((p) => {
        const zone = zoneOf(p.basis);
        const err = p.standardError ?? null;
        const lower = err === null || zone === "settled" ? null : Math.max(0, p.expectedTotal - err);
        const upper = err === null || zone === "settled" ? null : p.expectedTotal + err;
        return {
            date: p.pixelDate,
            zone,
            counted: zone === "forecast" ? null : p.soFar ?? (zone === "settled" ? p.expectedTotal : null),
            expected: zone === "settling" ? p.expectedTotal : null,
            forecast: zone === "forecast" ? p.expectedTotal : null,
            band: lower !== null && upper !== null ? [lower, upper] : null,
            lower,
            upper,
            marginPct: err !== null && zone !== "settled" && p.expectedTotal > 0 ? (err / p.expectedTotal) * 100 : null,
        };
    });
    for (let i = 1; i < rows.length; i += 1) {
        const prev = rows[i - 1];
        const cur = rows[i];
        if (prev.zone === "settled" && cur.zone === "settling") prev.expected = prev.counted;
        if (prev.zone !== "forecast" && cur.zone === "forecast") prev.forecast = prev.expected ?? prev.counted;
    }
    return rows;
};

const ZoneTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ payload?: IRow }> }) => {
    const row = payload?.[0]?.payload;
    if (!active || !row) return null;
    const range = row.lower !== null && row.upper !== null ? `${formatProjected(row.lower)} – ${formatProjected(row.upper)}` : null;
    const margin: [string, string][] = row.marginPct !== null ? [["Margin of error", `±${row.marginPct < 10 ? row.marginPct.toFixed(1) : Math.round(row.marginPct)}%`]] : [];
    const lines: [string, string][] =
        row.zone === "settled"
            ? [["Counted", formatProjected(row.counted ?? 0)]]
            : row.zone === "settling"
              ? [
                    ["Counted so far", formatProjected(row.counted ?? 0)],
                    ["Expected once settled", formatProjected(row.expected ?? 0)],
                    ...(range ? ([["Likely range", range]] as [string, string][]) : []),
                    ...margin,
                ]
              : [["Forecast", formatProjected(row.forecast ?? 0)], ...(range ? ([["Likely range", range]] as [string, string][]) : []), ...margin];
    const zoneLabel = { settled: "Settled", settling: "Still settling", forecast: "Forecast" }[row.zone];
    return (
        <Paper elevation={3} sx={{ p: 1.5, minWidth: 200 }}>
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5 }}>
                {dayjs(row.date).format("ddd, MMM D, YYYY")} · {zoneLabel}
            </Typography>
            {lines.map(([label, value]) => (
                <Box key={label} sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, fontFamily: numericFontFamily, fontVariantNumeric: "tabular-nums" }}>{value}</Typography>
                </Box>
            ))}
        </Paper>
    );
};

const LegendItem = ({ swatch, label }: { swatch: React.ReactNode; label: string }) => (
    <Box component="li" sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        {swatch}
        <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Box>
);

const lineSwatch = (color: string, dashed = false) => (
    <Box aria-hidden="true" sx={{ width: 18, height: 0, borderTop: `2px ${dashed ? "dashed" : "solid"} ${color}` }} />
);

interface IProjectionChartProps {
    eventLabel: string;
    points: IPredictionSeriesPoint[];
    from: string;
    to: string;
    height: number;
    compact?: boolean;
}

export const ProjectionChart = ({ eventLabel, points, from, to, height, compact }: IProjectionChartProps) => {
    const theme = useTheme();
    const patternId = `settling-${useId().replace(/:/g, "")}`;
    const rows = buildRows(points, from, to);
    const axisColor = theme.palette.text.secondary;
    const gridColor = theme.palette.divider;
    const firstSettling = rows.find((r) => r.zone === "settling")?.date ?? null;
    const firstForecast = rows.find((r) => r.zone === "forecast")?.date ?? null;
    const last = rows.length ? rows[rows.length - 1].date : null;
    const today = dayjs().format("YYYY-MM-DD");
    const todayInRange = rows.some((r) => r.date === today);
    const bandFill = theme.palette.mode === "dark" ? "rgba(165,180,252,0.18)" : "rgba(99,102,241,0.14)";

    const summary = `${eventLabel}, ${dayjs(from).format("MMM D")} to ${dayjs(to).format("MMM D")}. Solid line is counted, light line is the expected total for days still settling, dashed line is the forecast, and the shaded band is the likely range.`;

    if (!rows.length) {
        return (
            <Box sx={{ height, display: "grid", placeItems: "center" }}>
                <Typography color="text.secondary">No projection data for this period yet.</Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Box role="region" aria-label={summary} tabIndex={0} sx={{ width: "100%", overflowX: "auto" }}>
                <Box sx={{ minWidth: compact ? 560 : 640, height }}>
                    <ResponsiveContainer>
                        <ComposedChart data={rows} margin={{ top: 26, right: 12, bottom: 0, left: 0 }} accessibilityLayer>
                            <defs>
                                <pattern id={patternId} width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                                    <rect width="8" height="8" fill={theme.palette.action.hover} />
                                    <line x1="0" y1="0" x2="0" y2="8" stroke={theme.palette.text.disabled} strokeWidth="1.5" strokeOpacity="0.45" />
                                </pattern>
                            </defs>
                            <CartesianGrid strokeDasharray="4 4" stroke={gridColor} vertical={false} />
                            <XAxis
                                dataKey="date"
                                tickFormatter={(d: string) => dayjs(d).format("MMM D")}
                                tick={{ fontSize: 12, fill: axisColor, fontFamily: numericFontFamily }}
                                axisLine={{ stroke: gridColor }}
                                tickLine={false}
                                minTickGap={compact ? 48 : 32}
                            />
                            <YAxis
                                domain={[0, "auto"]}
                                width={64}
                                tick={{ fontSize: 11, fill: axisColor, fontFamily: numericFontFamily }}
                                axisLine={false}
                                tickLine={false}
                                tickFormatter={(v: number) => (v >= 1000 ? `${(v / 1000).toFixed(1)}K` : String(Math.round(v)))}
                                label={{ value: eventLabel, position: "top", fill: axisColor, fontSize: 11, offset: 12 }}
                            />
                            {firstSettling && (
                                <ReferenceArea x1={firstSettling} x2={firstForecast ?? last ?? firstSettling} fill={`url(#${patternId})`} fillOpacity={1} stroke="none"
                                    label={{ value: "Still settling", position: "insideTopLeft", fill: axisColor, fontSize: 11 }} />
                            )}
                            {firstForecast && last && (
                                <ReferenceArea x1={firstForecast} x2={last} fill={theme.palette.primary.main} fillOpacity={0.06} stroke="none"
                                    label={{ value: "Forecast", position: "insideTopRight", fill: axisColor, fontSize: 11 }} />
                            )}
                            {todayInRange && (
                                <ReferenceLine x={today} stroke={theme.palette.primary.main} strokeDasharray="3 3"
                                    label={{ value: "Today", position: "top", fill: theme.palette.primary.main, fontSize: 11 }} />
                            )}
                            <Area dataKey="band" stroke="none" fill={bandFill} isAnimationActive={false} connectNulls={false} activeDot={false} />
                            <Line dataKey="counted" stroke={PROJECTION_COLORS.counted} strokeWidth={2} dot={false} isAnimationActive={false} />
                            <Line dataKey="expected" stroke={PROJECTION_COLORS.expected} strokeWidth={2} dot={false} isAnimationActive={false} />
                            <Line dataKey="forecast" stroke={PROJECTION_COLORS.forecast} strokeWidth={2} strokeDasharray="6 4" dot={false} isAnimationActive={false} />
                            <Tooltip content={<ZoneTooltip />} cursor={{ stroke: gridColor }} />
                        </ComposedChart>
                    </ResponsiveContainer>
                </Box>
            </Box>
            <Box component="ul" aria-label="Chart legend" sx={{ listStyle: "none", p: 0, m: 0, mt: 1, display: "flex", flexWrap: "wrap", justifyContent: "center", columnGap: 2.5, rowGap: 0.5 }}>
                <LegendItem swatch={lineSwatch(PROJECTION_COLORS.counted)} label="Counted" />
                <LegendItem swatch={lineSwatch(PROJECTION_COLORS.expected)} label="Expected once settled" />
                <LegendItem swatch={lineSwatch(PROJECTION_COLORS.forecast, true)} label="Forecast" />
                <LegendItem swatch={<Box aria-hidden="true" sx={{ width: 14, height: 10, bgcolor: bandFill, border: 1, borderColor: "divider" }} />} label="Likely range" />
                <LegendItem
                    swatch={<Box aria-hidden="true" sx={{ width: 14, height: 10, border: 1, borderColor: "divider", background: `repeating-linear-gradient(45deg, ${theme.palette.text.disabled} 0 1.5px, transparent 1.5px 6px)` }} />}
                    label="Still settling"
                />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", mt: 1 }}>
                Solid is what has been counted; dashed is where the days ahead are heading. The wider the band, the wider the margin of error.
            </Typography>
        </Box>
    );
};
